/**
 * AmountRangeFilter Component
 * Dual-input component for filtering by amount range (min/max)
 * Displays in Indian Rupees (₹) with thousands formatting
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 3 (AC-4)
 */

import * as React from 'react';
import { IndianRupee, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// ============================================
// INTERFACE
// ============================================

export interface AmountRangeFilterProps {
  minAmount: number | null;
  maxAmount: number | null;
  onAmountRangeChange: (min: number | null, max: number | null) => void;
  className?: string;
}

// ============================================
// UTILITIES
// ============================================

/**
 * Format amount in Indian Rupee notation with commas
 * Example: 50000 → ₹50,000
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format amount in compact notation (K for thousands)
 * Example: 50000 → ₹50K
 */
function formatCurrencyCompact(amount: number): string {
  if (amount >= 10000000) {
    // 1 Crore and above
    return `₹${(amount / 10000000).toFixed(1)}Cr`;
  }
  if (amount >= 100000) {
    // 1 Lakh and above
    return `₹${(amount / 100000).toFixed(1)}L`;
  }
  if (amount >= 1000) {
    // 1 Thousand and above
    return `₹${(amount / 1000).toFixed(1)}K`;
  }
  return formatCurrency(amount);
}

/**
 * Validate amount range
 */
function validateAmountRange(
  min: number | null,
  max: number | null
): string | null {
  // Check for negative values
  if (min !== null && min < 0) {
    return 'Minimum amount cannot be negative';
  }

  if (max !== null && max < 0) {
    return 'Maximum amount cannot be negative';
  }

  // Check if min > max
  if (min !== null && max !== null && min > max) {
    return 'Minimum amount must be less than or equal to Maximum amount';
  }

  return null;
}

/**
 * Parse input value to number (handles empty strings and invalid inputs)
 */
function parseAmountInput(value: string): number | null {
  if (!value || value.trim() === '') return null;

  const parsed = parseFloat(value);
  if (isNaN(parsed) || parsed < 0) return null;

  return parsed;
}

// ============================================
// COMPONENT
// ============================================

export const AmountRangeFilter = React.memo(function AmountRangeFilter({
  minAmount,
  maxAmount,
  onAmountRangeChange,
  className,
}: AmountRangeFilterProps) {
  const [validationError, setValidationError] = React.useState<string | null>(null);

  // Local input state (for controlled inputs with string values)
  const [minInput, setMinInput] = React.useState(minAmount?.toString() || '');
  const [maxInput, setMaxInput] = React.useState(maxAmount?.toString() || '');

  // Sync local input state with props
  React.useEffect(() => {
    setMinInput(minAmount?.toString() || '');
  }, [minAmount]);

  React.useEffect(() => {
    setMaxInput(maxAmount?.toString() || '');
  }, [maxAmount]);

  // Validate whenever amounts change
  React.useEffect(() => {
    const error = validateAmountRange(minAmount, maxAmount);
    setValidationError(error);
  }, [minAmount, maxAmount]);

  // Handle min amount change
  const handleMinAmountChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setMinInput(value);

      const parsed = parseAmountInput(value);
      onAmountRangeChange(parsed, maxAmount);
    },
    [onAmountRangeChange, maxAmount]
  );

  // Handle max amount change
  const handleMaxAmountChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setMaxInput(value);

      const parsed = parseAmountInput(value);
      onAmountRangeChange(minAmount, parsed);
    },
    [onAmountRangeChange, minAmount]
  );

  // Clear amount range
  const handleClear = React.useCallback(() => {
    onAmountRangeChange(null, null);
    setMinInput('');
    setMaxInput('');
  }, [onAmountRangeChange]);

  const hasValue = minAmount !== null || maxAmount !== null;

  // Format range for badge display
  const formattedRange = React.useMemo(() => {
    if (!hasValue) return '';

    if (minAmount !== null && maxAmount !== null) {
      return `${formatCurrencyCompact(minAmount)} - ${formatCurrencyCompact(maxAmount)}`;
    }

    if (minAmount !== null) {
      return `From ${formatCurrencyCompact(minAmount)}`;
    }

    return `Up to ${formatCurrencyCompact(maxAmount!)}`;
  }, [minAmount, maxAmount, hasValue]);

  return (
    <div className={cn('space-y-2', className)}>
      {/* Label and Clear */}
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-foreground">
          <IndianRupee className="mr-1.5 inline-block h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Order Amount
        </Label>
        {hasValue && (
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Clear amount range"
          >
            <X className="inline-block h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Amount Inputs - Compact inline layout */}
      <div className="grid grid-cols-2 gap-2">
        {/* Min Amount */}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            ₹
          </span>
          <Input
            id="min-amount"
            type="number"
            min="0"
            step="100"
            value={minInput}
            onChange={handleMinAmountChange}
            placeholder="Min"
            className={cn(
              'pl-7 text-sm',
              validationError && 'border-destructive focus-visible:ring-destructive'
            )}
            aria-label="Minimum amount"
            aria-invalid={!!validationError}
            aria-describedby={validationError ? 'amount-range-error' : undefined}
          />
        </div>

        {/* Max Amount */}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            ₹
          </span>
          <Input
            id="max-amount"
            type="number"
            min="0"
            step="100"
            value={maxInput}
            onChange={handleMaxAmountChange}
            placeholder="Max"
            className={cn(
              'pl-7 text-sm',
              validationError && 'border-destructive focus-visible:ring-destructive'
            )}
            aria-label="Maximum amount"
            aria-invalid={!!validationError}
            aria-describedby={validationError ? 'amount-range-error' : undefined}
          />
        </div>
      </div>

      {/* Validation Error */}
      {validationError && (
        <p
          id="amount-range-error"
          className="text-xs text-destructive flex items-center gap-1"
          role="alert"
        >
          <span className="font-medium">Error:</span>
          {validationError}
        </p>
      )}

      {/* Selected Range Badge */}
      {hasValue && !validationError && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs font-normal">
            {formattedRange}
          </Badge>
        </div>
      )}

      {/* Accessibility announcements */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {validationError
          ? `Amount range error: ${validationError}`
          : hasValue
            ? `Amount filter applied: ${formattedRange}`
            : 'No amount filter applied'}
      </div>
    </div>
  );
});
